import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiErrorText } from "@/lib/apiError";
import { login, register } from "@/api/auth";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";
import { AuthLanguagePicker } from "@/components/AuthLanguagePicker";

interface PublicConfig {
  registration_enabled: boolean;
  environment: string;
}

const LANG_TILES: { code: AppLanguage; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState<AppLanguage>("ru");
  const [error, setError] = useState<ReactNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [cfgError, setCfgError] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const nav = useNavigate();

  useEffect(() => {
    api
      .get<PublicConfig>("/public/config")
      .then((r) => setCfg(r.data))
      .catch(() => setCfgError(true));
  }, []);

  // Preselect from browser locale and URL ?lang= override
  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang && (SUPPORTED_LANGUAGES as readonly string[]).includes(urlLang)) {
      handleLanguage(urlLang as AppLanguage);
      return;
    }
    const browser = navigator.language.slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(browser)) {
      handleLanguage(browser as AppLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLanguage(lang: AppLanguage) {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("mavinty_language", lang);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register(email, password, language);
      const token = await login(email, password);
      setAuth(token, email);
      // Straight to pricing, not to onboarding. Onboarding sits behind the
      // paywall, so a fresh account has to start its trial first — sending
      // them to /setup lands them on a screen that answers 402 to everything.
      nav("/pricing?welcome=1", { replace: true });
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      if (e.response?.status === 409) {
        setError(
          <>
            {t("auth.email_exists")}{" "}
            <Link to="/login" className="font-medium text-amber-dark underline">
              {t("auth.sign_in")}
            </Link>
          </>,
        );
      } else {
        setError(apiErrorText(err, t("auth.register_error")));
      }
    } finally {
      setLoading(false);
    }
  }

  const registrationClosed = cfg !== null && !cfg.registration_enabled;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <AuthLanguagePicker />
      <div className="mb-8 text-center">
        <h1 className="font-serif text-[34px] font-medium tracking-tight">{t("app.title")}</h1>
        <p className="mt-2 font-serif text-[15px] italic text-taupe">
          {t("app.tagline")}
        </p>
      </div>
      <Card className="space-y-4">
        {registrationClosed ? (
          <div className="space-y-3">
            <SectionLabel>{t("auth.registration_closed")}</SectionLabel>
            <p className="text-[14px] text-navy-deep">
              {t("auth.registration_closed_body")}
            </p>
            <div className="border-t border-cream-border pt-4 text-center text-[13px] text-taupe">
              {t("auth.have_account")}{" "}
              <Link to="/login" className="font-medium text-amber-dark hover:underline">
                {t("auth.sign_in")}
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Language picker — first thing, so the rest of the form renders in the chosen language */}
            <div>
              <label className="mb-2 block text-[12px] font-medium uppercase tracking-wider text-taupe">
                {t("auth.language")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {LANG_TILES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleLanguage(lang.code)}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      language === lang.code
                        ? "border-amber bg-amber/10 text-navy-deep"
                        : "border-cream-border text-taupe hover:bg-cream"
                    }`}
                  >
                    <div className="text-[22px] leading-none">{lang.flag}</div>
                    <div className="mt-1 text-[12px]">{lang.label}</div>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-taupe">
                {t("auth.language_help")}
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input
                type="email"
                label={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <Input
                type="password"
                label={t("auth.password_min")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              {error ? (
                <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
                  {error}
                </div>
              ) : null}
              {cfgError ? (
                <div className="rounded-lg bg-amber/10 px-3 py-2 text-[12px] text-amber-dark">
                  {t("auth.cfg_check_failed")}
                </div>
              ) : null}
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={loading}
                disabled={cfg === null && !cfgError}
              >
                {loading ? t("auth.creating_account") : t("auth.sign_up")}
              </Button>
            </form>
            <div className="border-t border-cream-border pt-4 text-center text-[13px] text-taupe">
              {t("auth.have_account")}{" "}
              <Link to="/login" className="font-medium text-amber-dark hover:underline">
                {t("auth.sign_in")}
              </Link>{" "}
              ·{" "}
              <Link
                to="/forgot-password"
                className="font-medium text-amber-dark hover:underline"
              >
                {t("auth.forgot_password")}
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
